import MENU_ITEMS from './menuConfig'

// ── Menu search index ────────────────────────────────────────────────────────
// Search used to prune the menu tree in place. That worked, but it inherited three
// problems from the tree shape:
//   • a level-3 hit kept its level-2 parent's *entire* child list, so matching one page
//     rendered ten — and the result you wanted was buried two levels down
//   • single substring match on one name, so word order mattered ("open ndr" hit,
//     "ndr open" missed) and a page was findable only by its exact current wording
//   • names repeat heavily across the menu — "Dashboard" appears four times, "Zone
//     Mapping", "Product Master", "Pickup Locations", "All Shipments", "Approval Queue"
//     and "COD Recon" all appear two or three times — and a pruned tree gave no way to
//     tell the copies apart
// This builds a flat index of every navigable leaf instead, each carrying the breadcrumb
// it lives under, so results render as a list.

// Short section names for the breadcrumb.
const SECTION_LABELS = {
  'D2C FREIGHT': 'D2C',
  'PTL FREIGHT': 'PTL',
  ADMIN: 'Admin',
}

// Synonyms and superseded wording, keyed by url. The menu was renamed and renamespaced
// (flat /closed-ndr became /ndr-npr/ndr/closed), so the words people type are often not
// the words currently on screen.
const KEYWORDS = {
  '/home': 'landing start overview',
  '/propilot': 'ai assistant copilot chat propilot',

  // D2C
  '/dashboard/d2c?tab=1': 'today overview kpi d2c',
  '/dashboard/d2c?tab=2': 'unprocessed pending orders',
  '/dashboard/d2c?tab=3': 'revenue realised earnings',
  '/dashboard/d2c?tab=4': 'shipments overall volume',
  '/d2c/orders/forward': 'forward shipment order awb list',
  '/d2c/orders/reverse': 'reverse rvp return shipment',
  '/d2c/orders/channel': 'channel webstore shopify woocommerce marketplace integration',
  '/d2c/orders/unprocessed-forward': 'unprocessed pending forward',
  '/d2c/orders/unprocessed-reverse': 'unprocessed pending reverse rvp',
  '/d2c/operations/cancellation-pending': 'cancel cancellation pending',
  '/d2c/operations/bulk-tracking': 'bulk track tracking awb upload',
  '/d2c/operations/manual-status-update': 'manual status update override',
  '/d2c/downloads/labels': 'download print label shipping',
  '/d2c/downloads/invoice': 'download print invoice bill',
  '/tools/escalation': 'escalation ticket complaint issue support',
  '/tools/escalation-v2': 'escalation ticket complaint issue support v2',

  // NDR / NPR
  '/ndr-npr/ndr/open': 'ndr non delivery undelivered open',
  '/ndr-npr/ndr/closed': 'ndr non delivery closed resolved',
  '/ndr-npr/ndr/bulk-upload': 'ndr bulk upload excel',
  '/ndr-npr/ndr/calls': 'ndr call recording audio ivr',
  '/ndr-npr/ndr/whatsapp': 'whatsapp wa automation ndr chat',
  '/ndr-npr/npr/open': 'npr non pickup open',
  '/ndr-npr/npr/closed': 'npr non pickup closed',
  '/prism/iedd': 'edd prism intelligent estimated delivery date',

  // Allocation
  '/pace/allocation': 'pace allocation rule courier assign',
  '/pace/allocations': 'pace allocation shadow run live',
  '/pace/analytics': 'pace analytics adherence manual commonality outcomes',

  // Serviceability
  '/tools/serviceability': 'serviceability pincode pin code check coverage',
  '/tools/bulk-serviceability': 'bulk serviceability pincode pin code',
  '/serviceability-intel/bulk-serviceability': 'bulk serviceability pincode pin code',
  '/serviceability-intel/coverage/delivery': 'coverage reach delivery pincode pan india',
  '/serviceability-intel/coverage/pickup': 'coverage reach pickup pincode',
  '/serviceability-intel/coverage/non-serviceable': 'non serviceable pincode blocked area',
  '/serviceability-intel/block/new': 'block pincode pin code new',
  '/serviceability-intel/block/active': 'blocked pincode pin code active',
  '/serviceability-intel/stuck-orders': 'stuck shipment exception delayed',

  // Weight
  '/weight/ledger': 'weight discrepancy dispute ledger chargeable volumetric',
  '/weight/merchant/all-discrepancy': 'weight discrepancy dispute mismatch',
  '/weight/v2/disputes': 'weight v2 dispute queue decide evidence hold accept reject courier',
  '/weight/v2/freeze-requests': 'weight v2 freeze request approve reject sku sub-category merchant weight freeze',
  '/weight/v2/settings': 'weight v2 dispute settings auto accept days tat merchant',
  '/weight/courier/mis-upload': 'courier mis upload weight',
  '/weight/courier/dws-config': 'dws weight machine config',

  // Tools
  '/tools/rate-calculator': 'rate price freight cost estimate calculator',
  '/tools/awb-mis': 'mis awb export download report',
  '/tools/awb-pod': 'pod proof of delivery awb signature',
  '/tools/sorter-image': 'sorter image photo scan',
  '/tools/carrier-logs': 'carrier log api request debug',
  '/tools/address-quality': 'netra address quality score',
  '/tools/rate-card-history': 'rate card history pricing tariff',
  '/tools/label-config': 'label config configuration print',
  '/tools/data-archival': 'data archival archive mongo s3 cold storage retention rehydrate collection',

  // Supply / courier
  '/supply/partners': 'courier partner directory list carrier',
  '/supply/agreements': 'agreement contract courier partner',
  '/supply/partners/update': 'rate tat update courier pricing',
  '/supply/zone-mapping': 'zone mapping pincode courier',
  '/merchant-config/zones/lookup': 'zone lookup merchant zone lane pincode',
  '/merchant-config/zones/override': 'zone override merchant lane pincode tat',
  '/merchant-config/zones/base-upload': 'base zone upload merchant zone map matrix PROZO_Z_MAP',
  '/supply/invoice-recon': 'invoice reconciliation courier recon',
  '/supply/cod-recon': 'cod reconciliation courier recon',
  '/courier-config/master': 'courier master carrier config legacy',
  '/courier-config/bulk-pincode-block': 'block pincode pin code bulk courier',

  // Merchant
  '/merchant-config/kyc-analytics': 'kyc verification document dashboard',
  '/merchant-config/merchants': 'merchant list all approved',
  '/merchant-config/pickup-drop-mapping': 'pickup drop mapping warehouse state pincode edd estimated delivery date shopify store location config',
  '/merchant-config/cod-config': 'cod config setting merchant',
  '/merchant-config/pricing-errors': 'pricing exception missing rate card no rate card',
  '/merchant-config/pickup-locations': 'pickup location warehouse address',
  '/merchant-config/delivery-locations': 'delivery location address drop',
  '/merchant-config/product-master': 'product master sku catalogue',
  '/merchant-config/pricing-setup': 'pricing setup onboarding rate',
  '/merchant-config/tat-setup': 'tat setup onboarding transit time',
  '/merchant-config/d2c-allocation': 'allocation setup onboarding courier',

  // Finance
  '/finance/merchant-invoice': 'merchant invoice billing gst',
  '/finance/courier-invoice': 'courier invoice billing',
  '/finance/wallet': 'wallet balance money config',
  '/finance/offline-payment': 'offline payment manual neft',
  '/finance/credit-limit': 'credit limit postpaid',
  '/finance/cod-reconciliation': 'cod reconciliation recon',
  '/finance/cod-remittance': 'cod remittance settlement payout',
  '/finance/awb-credit-note': 'credit note adjustment cn',
  '/finance/lost-damage': 'lost damaged claim compensation',
  '/finance/reports': 'report export mis download',
  '/finance/sms-wa-report': 'sms whatsapp vas billing communication',

  // Uploads
  '/uploads/serviceability': 'upload serviceability pincode',
  '/uploads/pods': 'pod proof of delivery upload',
  '/uploads/bulk-status': 'status upload missing bulk',
  '/uploads/edd': 'edd update upload delivery date',
  '/uploads/holiday-list': 'holiday calendar list',

  // PTL
  '/dashboard/ptl?tab=1': 'today overview kpi ptl b2b',
  '/ptl/pickups/quick': 'quick pickup request b2b create',
  '/ptl/pickups/detailed': 'detailed pickup request b2b create',
  '/ptl/pickups/all': 'pickup request list all b2b',
  '/ptl/shipments/all': 'ptl b2b shipment all list',
  '/ptl/shipments/open': 'ptl b2b shipment open',
  '/ptl/serviceability': 'ptl serviceability pincode pin code pickup drop b2b upload account code',
  '/ptl/oda': 'ptl oda out of delivery area km band distance extra days edd tat pincode courier',
  '/merchant-config/merchants?segment=ptl': 'ptl b2b merchant list merchant 360 profile',
  '/ptl/merchant-setup/onboarding': 'ptl merchant onboarding setup pricing rate card tat allocation b2b',
  '/ptl/merchant-setup/update': 'ptl merchant update rate card pricing tat allocation whitelist courier zone b2b',
  '/ptl/merchant-setup/zone-map': 'ptl merchant zone map standard override own pincode zone upload b2b merchant zone mapping',
  '/ptl/merchant-setup/tat': 'ptl merchant tat standard custom agreement lane matrix analytics faster slower edd b2b',
  '/ptl/shipments/under-process': 'stuck under process ptl b2b',
  '/ptl/shipments/non-abd': 'non abd ptl b2b shipment all list standard',
  '/ptl/shipments/non-abd/open': 'non abd ptl b2b shipment open',
  '/ptl/shipments/non-abd/under-process': 'non abd stuck under process ptl b2b',
  '/appointments/all': 'abd appointment based delivery all',
  '/appointments/edited': 'abd appointment edited edit reschedule log',
  '/appointments/overdue': 'abd appointment overdue pending open missed date passed not closed',
  '/ptl/uploads/tracking': 'tracking upload ptl b2b',
  '/ptl/downloads/labels': 'download label ptl b2b print',
  '/supply/ptl': 'ptl b2b courier partner setup',

  // Admin
  '/users/management': 'user team member access role',
  '/users/pii-access': 'pii access control privacy sensitive',
  '/users/audit-trails': 'audit trail log history',
  '/admin/webhooks': 'webhook api callback integration',
  '/admin/find-logs': 'api log request debug find',
  '/admin/config': 'platform setting config configuration',
  '/admin/clear-cache': 'cache clear application reset',
  '/admin/clear-wallet-cache': 'wallet cache clear reset',
  '/admin/bulk-cancellation': 'bulk cancel cancellation',
  '/admin/insurance-config': 'insurance config configuration',
  '/kci/dashboard': 'kci keep customers informed event dashboard',
}

// Pages that are routable and released but have no menu entry. Deliberately curated, not
// swept: 130 of the unlisted routes are <Navigate> aliases for old bookmarks (indexing
// those would show every page twice), and the /supply/rate-cards/* module is commented out
// of menuConfig on purpose pending sign-off — search must not leak it. Roles here mirror
// the ProtectedRoute guard on each route in PrivateRoute.js.
const UNLISTED = [
  {url: '/rate-card', name: 'Rate Card', parent: 'Courier Setup', role: ['INTERNAL'], keywords: 'rate card pricing tariff'},
  {url: '/qc-config', name: 'QC Configuration', parent: 'Utilities & Tools', role: ['SA'], keywords: 'qc quality check config'},
  {url: '/whats-new', name: 'Release Notes', parent: 'Admin', keywords: 'release notes whats new changelog version'},
  {url: '/admin/channel-config', name: 'Channel Config', parent: 'User & Access', role: ['SA'], keywords: 'channel config integration webstore'},
  {url: '/finance/reports/create', name: 'Create Report', parent: 'Reports & Analytics', role: ['REPORTS'], keywords: 'create new report export'},
  {url: '/merchant-config/onboarding/setup-kyc', name: 'KYC Upload', parent: 'Merchant Setup', role: ['MERCHANT_ONBOARDING', 'INTERNAL'], keywords: 'kyc upload document onboarding verification'},
  {url: '/merchant-config/pricing-update', name: 'Update Merchant Pricing', parent: 'Merchant Setup', role: ['MERCHANT_ONBOARDING', 'INTERNAL'], keywords: 'update pricing rate merchant wizard'},
  {url: '/bulk-invoice-generate', name: 'Bulk Invoice Generate', parent: 'Finance & Billing', role: ['INTERNAL'], keywords: 'bulk invoice generate label'},
  {url: '/rate-calculator-ptl', name: 'PTL Rate Calculator', parent: 'PTL', keywords: 'rate price freight calculator ptl b2b estimate'},
  {url: '/tracking-list', name: 'Tracking List', parent: 'Utilities & Tools', keywords: 'track tracking awb list result'},
]

const norm = (s) => (s || '').toLowerCase()

const breadcrumb = (...parts) => parts.filter(Boolean).join(' · ')

/**
 * Flat list of every navigable destination in the menu.
 *
 * Only leaves are indexed. A menu node that has children renders as a `div`, not a `Link`
 * (see SidebarNew), so it is not a place you can navigate to — its name feeds the
 * breadcrumb and the search context instead.
 *
 * `roleChain` collects the `role` array at every level down to the leaf. The tree gets the
 * same effect implicitly — a nested item only renders because its parent rendered — so the
 * flat index has to re-apply each ancestor's gate explicitly or it would surface pages the
 * tree hides.
 */
export function buildMenuSearchIndex() {
  const entries = []
  const seen = new Set()

  const push = (entry) => {
    if (!entry.url) return
    const key = `${entry.name}|${entry.url}`
    if (seen.has(key)) return // same page listed under two groups — keep the first
    seen.add(key)
    entries.push(entry)
  }

  let section = ''

  MENU_ITEMS.forEach((top) => {
    if (top.type === 'section') {
      section = SECTION_LABELS[top.name] || top.name
      return
    }
    if (top.type) return // divider

    const sectionLabel = section

    const leaf = (item, parents, roleChain) =>
      push({
        url: item.url,
        name: item.name,
        breadcrumb: breadcrumb(sectionLabel, ...parents),
        icon: top.icon,
        tag: top.tag,
        helper: item.helper,
        rbacPersona: item.rbacPersona,
        badgeKey: item.badgeKey,
        badgeBg: item.badgeBg,
        badgeColor: item.badgeColor,
        roleChain,
        terms: norm([item.name, KEYWORDS[item.url], item.url].filter(Boolean).join(' ')),
        // Group *names* only — never the group's KEYWORDS. A group's url is always equal to
        // one of its descendants' urls, so keying synonyms off it would hand every sibling
        // that descendant's words: with '/ndr-npr/ndr/open' on the group, searching
        // "ndr open" matched all 20 pages under Delivery Performance instead of Open NDR.
        parentName: norm([...parents, sectionLabel].filter(Boolean).join(' ')),
      })

    if (!top.submenu?.length) {
      leaf(top, [], [top.role])
      return
    }

    top.submenu.forEach((sub) => {
      if (sub?.type) return
      if (!sub.submenu?.length) {
        leaf(sub, [top.name], [top.role, sub.role])
        return
      }
      sub.submenu.forEach((nested) => {
        if (nested?.type) return
        leaf(nested, [top.name, sub.name], [top.role, sub.role, nested.role])
      })
    })
  })

  UNLISTED.forEach((u) =>
    push({
      url: u.url,
      name: u.name,
      breadcrumb: u.parent,
      roleChain: [u.role],
      terms: norm([u.name, u.keywords, u.url].filter(Boolean).join(' ')),
      parentName: norm(u.parent),
    })
  )

  return entries
}

/**
 * Tokenised AND match: every word must appear in the leaf's own terms or in its breadcrumb,
 * so word order stops mattering ("ndr open" now finds the same page as "open ndr") and
 * naming a group narrows it to that group ("finance cod", "ptl pickup").
 *
 * Ranking puts *every* query word being in the name above the name merely starting with the
 * first word. The other way round, "ndr open" ranked "NDR Bulk Upload" (starts with "ndr")
 * above "Open NDR" (matches both words) — the prefix bonus outweighed the real hit.
 */
export function searchMenu(index, query) {
  const tokens = norm(query).trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return []

  const scored = []
  index.forEach((entry) => {
    const haystack = `${entry.terms} ${entry.parentName}`
    if (!tokens.every((t) => haystack.includes(t))) return

    const name = norm(entry.name)
    let score = 0
    if (tokens.every((t) => name.includes(t))) score += 8
    else if (tokens.some((t) => name.includes(t))) score += 1
    if (name.startsWith(tokens[0])) score += 4
    scored.push({entry, score})
  })

  return scored
    .sort((a, b) => b.score - a.score || a.entry.name.localeCompare(b.entry.name))
    .map((s) => s.entry)
}
